import { apiVisitEngine } from '@/shared/api/client'
import type {
  IBloodCollectionAnalysis,
  IBloodCollectionApiResponse,
  IBloodCollectionBarcode,
  IBloodCollectionBarcodePrintData,
  IBloodCollectionDetail,
  IBloodCollectionGenerateBarcodesPayload,
  IBloodCollectionParams,
  IBloodCollectionPatchAnalysesPayload,
  IBloodCollectionQueueParams,
  IBloodCollectionQueueResponse,
  IBloodCollectionRejectPayload,
  IBloodCollectionResponse,
  IBloodCollectionStageActionData,
} from '../model/blood-collection.type'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/query-keys'

// ── Raw API ───────────────────────────────────────────────────────────────────

export const bloodCollectionApi = {
  getBloodCollectionQueue: async (
    params: IBloodCollectionQueueParams,
  ): Promise<IBloodCollectionQueueResponse> => {
    const query = new URLSearchParams()
    query.set('page', String(params.page))
    query.set('size', String(params.size))
    if (params.visit_type_id) query.set('visit_type_id', params.visit_type_id)
    if (params.status) query.set('status', params.status)
    if (params.donation_type_id)
      query.set('donation_type_id', params.donation_type_id)
    if (params.donor_category_id)
      query.set('donor_category_id', params.donor_category_id)
    if (params.gender) query.set('gender', params.gender)
    if (params.is_mobile_collection !== undefined)
      query.set('is_mobile_collection', String(params.is_mobile_collection))
    if (params.search) query.set('search', params.search)

    const response = await apiVisitEngine.get<IBloodCollectionQueueResponse>(
      `/v1/blood-collection/queue?${query.toString()}`,
    )
    return response.data
  },

  getBloodCollection: async (
    params: IBloodCollectionParams,
  ): Promise<IBloodCollectionResponse> => {
    const query = new URLSearchParams()
    query.set('page', String(params.page))
    query.set('size', String(params.size))
    if (params.search) query.set('search', params.search)
    if (params.visit_type) query.set('visit_type', params.visit_type)
    if (params.donation_type_id)
      query.set('donation_type_id', params.donation_type_id)
    if (params.donor_category_id)
      query.set('donor_category_id', params.donor_category_id)
    if (params.status) query.set('status', params.status)
    if (params.gender) query.set('gender', params.gender)
    if (params.date_from) query.set('date_from', params.date_from)
    if (params.date_to) query.set('date_to', params.date_to)

    const response = await apiVisitEngine.get<IBloodCollectionResponse>(
      `/v1/blood-collection?${query.toString()}`,
    )
    return response.data
  },

  getBloodCollectionById: async (
    collectionStageId: string,
  ): Promise<IBloodCollectionApiResponse<IBloodCollectionDetail>> => {
    const response = await apiVisitEngine.get<
      IBloodCollectionApiResponse<IBloodCollectionDetail>
    >(`/v1/blood-collection/${collectionStageId}`, {
      _skipGlobalErrorHandler: true,
    } as any)
    return response.data
  },

  rejectBloodCollection: async (
    collectionStageId: string,
    payload: IBloodCollectionRejectPayload,
  ): Promise<IBloodCollectionApiResponse<IBloodCollectionStageActionData>> => {
    const response = await apiVisitEngine.post<
      IBloodCollectionApiResponse<IBloodCollectionStageActionData>
    >(`/v1/blood-collection/${collectionStageId}/reject`, payload)
    return response.data
  },

  completeBloodCollection: async (
    collectionStageId: string,
  ): Promise<IBloodCollectionApiResponse<IBloodCollectionStageActionData>> => {
    const response = await apiVisitEngine.post<
      IBloodCollectionApiResponse<IBloodCollectionStageActionData>
    >(`/v1/blood-collection/${collectionStageId}/complete`, undefined, {
      _skipGlobalErrorHandler: true,
    } as any)
    return response.data
  },

  generateBarcodes: async (
    collectionStageId: string,
    payload: IBloodCollectionGenerateBarcodesPayload,
  ): Promise<IBloodCollectionApiResponse<IBloodCollectionBarcode[]>> => {
    const response = await apiVisitEngine.post<
      IBloodCollectionApiResponse<IBloodCollectionBarcode[]>
    >(`/v1/blood-collection/${collectionStageId}/barcodes/generate`, payload, {
      _skipGlobalErrorHandler: true,
    } as any)
    return response.data
  },

  printBarcode: async (
    barcodeId: string,
  ): Promise<IBloodCollectionApiResponse<IBloodCollectionBarcodePrintData>> => {
    const response = await apiVisitEngine.post<
      IBloodCollectionApiResponse<IBloodCollectionBarcodePrintData>
    >(`/v1/blood-collection/barcodes/${barcodeId}/print`)
    return response.data
  },

  patchAnalyses: async (
    collectionStageId: string,
    payload: IBloodCollectionPatchAnalysesPayload,
  ): Promise<IBloodCollectionApiResponse<IBloodCollectionAnalysis[]>> => {
    const response = await apiVisitEngine.patch<
      IBloodCollectionApiResponse<IBloodCollectionAnalysis[]>
    >(`/v1/blood-collection/${collectionStageId}/analyses`, payload, {
      _skipGlobalErrorHandler: true,
    } as any)
    return response.data
  },
}

export const useGetBloodCollectionQueueQuery = (
  params: IBloodCollectionQueueParams,
  { enabled }: { enabled: boolean },
) =>
  useQuery({
    queryKey: queryKeys.bloodCollection.queue(params),
    queryFn: () => bloodCollectionApi.getBloodCollectionQueue(params),
    enabled,
  })

export const useGetBloodCollectionQuery = (
  params: IBloodCollectionParams,
  { enabled }: { enabled: boolean },
) =>
  useQuery({
    queryKey: queryKeys.bloodCollection.list(params),
    queryFn: () => bloodCollectionApi.getBloodCollection(params),
    enabled,
  })

export const useGetBloodCollectionByIdQuery = (
  collectionStageId: string,
  { enabled }: { enabled: boolean },
) =>
  useQuery({
    queryKey: queryKeys.bloodCollection.detail(collectionStageId),
    queryFn: () => bloodCollectionApi.getBloodCollectionById(collectionStageId),
    enabled: enabled && !!collectionStageId,
  })

export const useRejectBloodCollectionMutation = (collectionStageId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: IBloodCollectionRejectPayload) =>
      bloodCollectionApi.rejectBloodCollection(collectionStageId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.bloodCollection.detail(collectionStageId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.bloodCollection.all(),
      })
    },
  })
}

export const useCompleteBloodCollectionMutation = (
  collectionStageId: string,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      bloodCollectionApi.completeBloodCollection(collectionStageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.bloodCollection.detail(collectionStageId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.bloodCollection.all(),
      })
    },
  })
}

export const useGenerateBarcodesMutation = (collectionStageId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: IBloodCollectionGenerateBarcodesPayload) =>
      bloodCollectionApi.generateBarcodes(collectionStageId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.bloodCollection.detail(collectionStageId),
      })
    },
  })
}

export const usePrintBarcodeMutation = (collectionStageId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (barcodeId: string) =>
      bloodCollectionApi.printBarcode(barcodeId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.bloodCollection.detail(collectionStageId),
      })
    },
  })
}

export const usePatchBloodCollectionAnalysesMutation = (
  collectionStageId: string,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: IBloodCollectionPatchAnalysesPayload) =>
      bloodCollectionApi.patchAnalyses(collectionStageId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.bloodCollection.detail(collectionStageId),
      })
    },
  })
}
